import { FC, useState } from "react";

import { showAddSectionModal } from "../helpers";

import { LoadingScreen } from "components/LoadingScreen";
import { EditableText } from "components/hover-widgets/EditableText";
import { AdminSectionListPanel } from "components/materials/home/admin/content/AdminSectionList";
import { TermOptionsMenu } from "components/menus/TermOptionsMenu";
import { TermType } from "components/server-types";
import { t } from "i18n/i18n";
import { AccordionTitle } from "mds/components/AccordionTitle";
import { Banner } from "mds/components/Banner";
import { IconText } from "mds/components/IconText";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { AddPlusIcon, CalendarIcon, EyeNotVisibleIcon, FolderIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppDispatch } from "store/index";
import {
  courseGroupingSelectors,
  courseSelectors,
  selectCourseIdsByGroupingIdAndTerm,
  selectGroupingIdsWithCoursesByTerm,
} from "store/selectors";
import { localStateActions } from "store/slices/local";
import { useListSelector, useObjectSelector } from "store/store-hooks";
import { ADMIN_TERM_EVENT_PREFIX } from "utils/amplitude";
import { getCourseDisplayTitle } from "utils/course";
import { useSectionCloneObserver } from "utils/hooks/use-section-clone-observer";
import { onTitleChanged } from "utils/store-utils";

type TermCourseSectionPanelProps = {
  courseId: string;
  termId: string;
  termTitle: string;
  canEdit: boolean;
  className?: string;
};

const TermCourseSectionPanel: FC<TermCourseSectionPanelProps> = ({
  courseId,
  termId,
  termTitle,
  canEdit,
  className,
}) => {
  const course = useObjectSelector(
    (state) => courseSelectors.selectById(state, courseId),
    ["code", "title"],
  );

  if (!course) {
    return null;
  }

  return (
    <div className={className}>
      <AdminSectionListPanel
        canEdit={canEdit}
        courseId={courseId}
        eventPrefix={ADMIN_TERM_EVENT_PREFIX}
        eventProperties={{ course: course.code, term: termTitle }}
        termId={termId}
        title={getCourseDisplayTitle(course)}
      />
    </div>
  );
};

type TermGroupingSectionProps = {
  groupingId: string;
  termId: string;
  termTitle: string;
  canEdit: boolean;
};

const TermGroupingSection: FC<TermGroupingSectionProps> = ({
  groupingId,
  termId,
  termTitle,
  canEdit,
}) => {
  const grouping = useObjectSelector(
    (state) => courseGroupingSelectors.selectById(state, groupingId),
    ["title"],
  );
  const courseIds = useListSelector((state) =>
    selectCourseIdsByGroupingIdAndTerm(state, termId, groupingId),
  );

  if (!grouping) {
    return null;
  }

  return (
    <AccordionTitle
      headerClassName="ml-2 w-[calc(100%-12px)] justify-between border-b border-black-tint-90 pr-4 mb-3"
      iconPosition="end"
      title={
        <div className="flex items-center gap-2">
          <FolderIcon className="text-blue" size="m" />
          <span className="body-m font-medium">{grouping.title}</span>
        </div>
      }
      startOpen
    >
      {courseIds.map((courseId) => (
        <TermCourseSectionPanel
          canEdit={canEdit}
          className="ml-8"
          courseId={courseId}
          key={courseId}
          termId={termId}
          termTitle={termTitle}
        />
      ))}
    </AccordionTitle>
  );
};

type SectionDetailCourseViewProps = {
  term: TermType;
  canEdit: boolean;
};

export const SectionDetailCourseView: FC<SectionDetailCourseViewProps> = ({ term, canEdit }) => {
  const dispatch = useAppDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const { canAuthorOrg } = useCapabilities();
  const groupingIds = useListSelector((state) =>
    selectGroupingIdsWithCoursesByTerm(state, term.id),
  );
  const ungroupedCourseIds = useListSelector((state) =>
    selectCourseIdsByGroupingIdAndTerm(state, term.id, null),
  );

  useSectionCloneObserver();

  const handleEnableEditMode = () => {
    dispatch(localStateActions.setIsEditMode(true));
  };

  if (!term) {
    return <LoadingScreen>{t("home.settings.no_terms")}</LoadingScreen>;
  }

  const handleAddSection = () =>
    showAddSectionModal({
      defaultTermId: term.id,
      eventName: `${ADMIN_TERM_EVENT_PREFIX}Add Section`,
    });

  const showContent = groupingIds.length > 0 || ungroupedCourseIds.length > 0;

  return (
    <div className="px-0 py-3 text-black-tint-20 md:px-7">
      <h2 className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {term.archived_at ? (
            <EyeNotVisibleIcon className="text-blue" size="l" />
          ) : (
            <CalendarIcon className="text-blue" size="l" />
          )}

          <div className="flex items-center">
            <EditableText
              isEditing={isEditing}
              label={t("glossary.term_title")}
              textSize="h2"
              value={
                term.archived_at && !isEditing
                  ? t("home.terms.archived_term_format", { term: term.title })
                  : term.title
              }
              onSetIsEditing={setIsEditing}
              onValueChanged={(newTitle) => onTitleChanged("terms", term.id, newTitle)}
            />
          </div>
        </div>

        {canEdit && (
          <div className="flex items-center gap-2">
            <SquareButton
              eventName={`${ADMIN_TERM_EVENT_PREFIX}Add Section`}
              kind="secondary"
              size="s"
              onClick={handleAddSection}
            >
              <IconText iconStart={<AddPlusIcon />} text={t("common.add.section")} />
            </SquareButton>

            {canAuthorOrg && (
              <TermOptionsMenu
                eventPrefix={ADMIN_TERM_EVENT_PREFIX}
                term={term}
                onRename={() => setIsEditing(true)}
              />
            )}
          </div>
        )}
      </h2>

      {!showContent ? (
        <Banner
          actionButtonText={canEdit ? t("common.add.section") : t("warning.enable_edit_mode")}
          eventName={
            canEdit
              ? `${ADMIN_TERM_EVENT_PREFIX}Add Section`
              : `${ADMIN_TERM_EVENT_PREFIX}Enable Edit Mode`
          }
          kind="info"
          onAction={canEdit ? handleAddSection : handleEnableEditMode}
        >
          {canEdit
            ? t("home.terms.empty_term_detail_banner_text")
            : t("home.terms.empty_term_detail_banner_text_no_edit")}
        </Banner>
      ) : (
        <>
          {groupingIds.map((groupingId) => (
            <TermGroupingSection
              canEdit={canEdit}
              groupingId={groupingId}
              key={groupingId}
              termId={term.id}
              termTitle={term.title}
            />
          ))}

          {ungroupedCourseIds.map((courseId) => (
            <TermCourseSectionPanel
              canEdit={canEdit}
              className="ml-2"
              courseId={courseId}
              key={courseId}
              termId={term.id}
              termTitle={term.title}
            />
          ))}
        </>
      )}
    </div>
  );
};
